/**
 * Date Range Picker Renderer
 *
 * Date range picker with start and end date inputs
 */

import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import * as LucideIcons from 'lucide-react';
import type { FieldRenderer } from '@/MyCode/core';

/**
 * Date Range Picker Renderer
 * Features:
 * - Separate start and end date inputs
 * - Calendar icons on both inputs
 * - Min/max date constraints
 * - Cross-validation (start <= end)
 * - Error states
 */
export const renderDateRangePicker: FieldRenderer = ({ field, hasError, handleChange, isRequired, formData }) => {
    // Parse the range value - expected format: { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' }
    // Or store as separate fields: fieldName_start and fieldName_end
    const startFieldName = `${field.name}_start`;
    const endFieldName = `${field.name}_end`;
    const startValue = formData[startFieldName] || '';
    const endValue = formData[endFieldName] || '';

    const CalendarIcon = LucideIcons.Calendar;

    return (
        <div className="space-y-2">
            <Label htmlFor={field.name}>
                {field.label}
                {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            <div className="flex items-center gap-2">
                <div className="relative flex items-center flex-1">
                    <Input
                        id={startFieldName}
                        type="date"
                        value={startValue}
                        onChange={(e) => handleChange(startFieldName, e.target.value)}
                        min={field.minValue as string}
                        max={endValue || (field.maxValue as string)}
                        className={`pe-9 ${hasError ? 'border-destructive' : ''}`}
                        placeholder="Start date"
                    />
                    <div className="pointer-events-none absolute inset-y-0 end-0 flex items-center justify-center pe-3 text-muted-foreground/80">
                        <CalendarIcon size={16} aria-hidden="true" />
                    </div>
                </div>
                <span className="text-muted-foreground/70">-</span>
                <div className="relative flex items-center flex-1">
                    <Input
                        id={endFieldName}
                        type="date"
                        value={endValue}
                        onChange={(e) => handleChange(endFieldName, e.target.value)}
                        min={startValue || (field.minValue as string)}
                        max={field.maxValue as string}
                        className={`pe-9 ${hasError ? 'border-destructive' : ''}`}
                        placeholder="End date"
                    />
                    <div className="pointer-events-none absolute inset-y-0 end-0 flex items-center justify-center pe-3 text-muted-foreground/80">
                        <CalendarIcon size={16} aria-hidden="true" />
                    </div>
                </div>
            </div>
            {hasError && (
                <p className="text-sm text-destructive">
                    {/* Error message would be displayed here */}
                </p>
            )}
        </div>
    );
};

export default renderDateRangePicker;
